import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  UsersSelectPaginated,
  mapUsers,
} from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { BaseModalProps, Button, ModalContentWrapper, SelectPaginateOption, TextInput } from 'ui';
import { UpdateVaultRequest, VaultResponse } from 'compliance-api';

interface EditVaultModalProps extends BaseModalProps {
  vault: VaultResponse;
  onSave: (payload: UpdateVaultRequest) => Promise<void>;
}

const EditVaultModal = ({ open, onClose, vault, onSave }: EditVaultModalProps) => {
  const [name, setName] = useState(vault.name);
  const [description, setDescription] = useState(vault.description || '');
  const [sharedUsers, setSharedUsers] = useState<SelectPaginateOption<number>[]>(
    mapUsers((vault.shared_users || []).filter(user => user.id !== vault.owner?.id)),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setName(vault.name);
    setDescription(vault.description || '');
    setSharedUsers(
      mapUsers((vault.shared_users || []).filter(user => user.id !== vault.owner?.id)),
    );
  }, [vault]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Vault name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        vault_id: vault.id,
        name: name.trim(),
        description: description.trim() || undefined,
        shared_user_ids: sharedUsers.map(user => user.value),
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContentWrapper
      open={open}
      title="Edit vault"
      onClose={handleClose}
      variant="center"
      classes={{ dialogPanel: 'max-w-xl' }}
    >
      <div className="px-6 pb-6 pt-2 space-y-4">
        <TextInput
          name="vault_name"
          label="Vault name"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <TextInput
          name="vault_description"
          label="Description"
          textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          className="min-h-[90px]"
        />
        <UsersSelectPaginated
          isMulti
          label="Shared users"
          placeholder="Select users"
          value={sharedUsers}
          onChange={options => setSharedUsers(options || [])}
          isClearable
          excludeUserIds={vault.owner?.id ? [vault.owner.id] : []}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </div>
    </ModalContentWrapper>
  );
};

export default EditVaultModal;
